import React from 'react';

const getLaunchStatusLabel = (launchState) => ({
  'creating-room': 'Creating your local live room...',
}[launchState] || '');

const getMacOfflineNote = (platform) => (
  platform === 'mac'
    ? 'On macOS, open GoLive Helper.app once manually first. If macOS blocks it, go to System Settings > Privacy & Security and choose Open Anyway.'
    : ''
);

const GoLiveLaunchDialog = ({
  isOpen = false,
  launchState = 'idle',
  errorMessage = '',
  downloadUrl = '',
  localRelayPhase = 'checking',
  localRelayMessage = 'Checking for local relay...',
  platform = 'windows',
  onClose,
  onLaunchLocal,
} = {}) => {
  if (!isOpen) return null;

  const isBusy = launchState === 'creating-room';
  const isLocalRelayReady = localRelayPhase === 'ready';
  const showStatusStrip = launchState === 'creating-room' || launchState === 'error';
  const statusText = launchState === 'error'
    ? (errorMessage || 'The GoLive Helper could not be reached.')
    : getLaunchStatusLabel(launchState);
  const macOfflineNote = getMacOfflineNote(platform);
  const localCardClassName = `go-live-choice-card ${isLocalRelayReady ? 'go-live-choice-card-active' : 'go-live-choice-card-muted'}`.trim();
  const localAvailabilityClassName = `go-live-local-availability is-${localRelayPhase}`.trim();

  return (
    <div className="go-live-overlay" role="presentation">
      <div className="go-live-modal" role="dialog" aria-modal="true" aria-labelledby="go-live-modal-title">
        <div className="go-live-header">
          <div>
            <h3 id="go-live-modal-title">Choose Go Live Mode</h3>
            <p>Local mode is available today. Online mode is reserved for the future paid relay option.</p>
          </div>
          <button
            type="button"
            className="secondary-btn ghost"
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close Go Live chooser"
          >
            Close
          </button>
        </div>

        <div className="go-live-choice-grid">
          <section className={localCardClassName}>
            <div className="go-live-choice-copy">
              <span className="go-live-choice-badge">Free</span>
              <h4>Local</h4>
              <p>Runs the WebSocket relay on this computer using the GoLive Helper.</p>
            </div>

            <div className={localAvailabilityClassName} role="status" aria-live="polite">
              <span className="go-live-local-availability-label">
                {localRelayPhase === 'ready'
                  ? 'Relay ready'
                  : localRelayPhase === 'offline'
                    ? 'Relay offline'
                    : 'Checking relay'}
              </span>
              <p>{localRelayMessage}</p>
              {localRelayPhase === 'offline' && macOfflineNote ? (
                <p className="go-live-helper-note">{macOfflineNote}</p>
              ) : null}
            </div>

            <div className="go-live-choice-actions">
              <button
                type="button"
                className="export-btn primary go-live-choice-action"
                onClick={onLaunchLocal}
                disabled={isBusy || !isLocalRelayReady}
              >
                {launchState === 'creating-room' ? 'Creating Room...' : 'Use local mode'}
              </button>

              {downloadUrl ? (
                <a className="secondary-btn go-live-choice-action go-live-download-link" href={downloadUrl} target="_blank" rel="noreferrer">
                  Download
                </a>
              ) : (
                <button type="button" className="secondary-btn go-live-choice-action" disabled>Download</button>
              )}
            </div>
          </section>

          <section className="go-live-choice-card go-live-choice-card-disabled">
            <div className="go-live-choice-copy">
              <span className="go-live-choice-badge go-live-choice-badge-muted">Coming soon</span>
              <h4>Online</h4>
              <p>Cloud relay hosting for subscription users will plug into this same chooser later.</p>
            </div>
            <button
              type="button"
              className="secondary-btn go-live-choice-action"
              disabled
            >
              Online Unavailable
            </button>
          </section>
        </div>

        {showStatusStrip ? (
          <div className={`go-live-status-strip ${launchState === 'error' ? 'is-error' : ''}`.trim()}>
            <strong>{launchState === 'error' ? 'Helper issue:' : 'Status:'}</strong>
            <span>{statusText}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GoLiveLaunchDialog;
